import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Tutorial } from './models';
import { HelpSidebarComponent } from './components/help-sidebar/help-sidebar.component';

@Component({
  selector: 'app-help-tutorials',
  templateUrl: './help-tutorials.component.html',
  styleUrls: ['./help-tutorials.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HelpTutorialsComponent {
  @ViewChild('helpSidebar') helpSidebar!: HelpSidebarComponent;

  isSidebarOpen = false;
  viewerTutorial: Tutorial | null = null;
  viewerStepIndex = 0;

  private savedTutorial: Tutorial | null = null;
  private savedStepIndex = 0;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  /** True when a dismissed tutorial can be resumed from a saved step */
  get canResume(): boolean {
    return !!this.savedTutorial;
  }

  /** Label shown on the FAB when a tutorial can be resumed */
  get resumeStepLabel(): string {
    if (!this.savedTutorial) { return ''; }
    const total = this.savedTutorial.Steps?.length || 0;
    return `Resume · Step ${this.savedStepIndex + 1} of ${total}`;
  }

  /** FAB click: resume saved zoomed state, or toggle sidebar */
  toggleSidebar(): void {
    if (this.savedTutorial) {
      this.viewerTutorial = this.savedTutorial;
      this.viewerStepIndex = this.savedStepIndex;
      this.isSidebarOpen = false;
      this.savedTutorial = null;
      this.cdr.markForCheck();

      // Also sync sidebar to the saved step
      if (this.helpSidebar) {
        this.helpSidebar.selectTutorialAndStep(this.viewerTutorial, this.viewerStepIndex);
      }
      return;
    }

    this.isSidebarOpen = !this.isSidebarOpen;
    this.cdr.markForCheck();
  }

  /** Closes the sidebar */
  closeSidebar(): void {
    this.isSidebarOpen = false;
    this.cdr.markForCheck();
  }

  /** Opens the full-screen step viewer */
  onOpenViewer(event: { tutorial: Tutorial; stepIndex: number }): void {
    this.viewerTutorial = event.tutorial;
    this.viewerStepIndex = event.stepIndex;
    this.savedTutorial = null; // Clear saved state when explicitly opening
    this.isSidebarOpen = false; // Auto-close the sidebar so nothing shows behind the viewer
    this.cdr.markForCheck();
  }

  /** Syncs sidebar step in real-time when the zoomed viewer navigates */
  onViewerStepChange(stepIndex: number): void {
    this.viewerStepIndex = stepIndex;
    if (this.helpSidebar) {
      this.helpSidebar.resumeFromStep(stepIndex);
    }
  }

  /** X button: closes only the zoomed viewer, sidebar stays open */
  onCloseViewer(lastStepIndex: number): void {
    this.viewerStepIndex = lastStepIndex;
    this.viewerTutorial = null;
    this.savedTutorial = null; // No resume needed — sidebar is still open
    this.cdr.markForCheck();
    if (this.helpSidebar) {
      this.helpSidebar.resumeFromStep(lastStepIndex);
    }
  }

  /** Click on empty area: dismiss everything, save state for FAB resume */
  onDismissAll(lastStepIndex: number): void {
    // Save state so FAB can resume later
    if (this.viewerTutorial) {
      this.savedTutorial = this.viewerTutorial;
      this.savedStepIndex = lastStepIndex;
    }

    this.viewerStepIndex = lastStepIndex;
    this.viewerTutorial = null;
    this.isSidebarOpen = false;
    this.cdr.markForCheck();
    if (this.helpSidebar) {
      this.helpSidebar.resumeFromStep(lastStepIndex);
    }
  }

  /** List icon: close viewer and go back to the tutorials list in the sidebar */
  onBackToList(): void {
    this.viewerTutorial = null;
    this.savedTutorial = null;
    this.isSidebarOpen = true;
    this.cdr.markForCheck();
    if (this.helpSidebar) {
      this.helpSidebar.backToList();
    }
  }
}
